import {BreakKey, sanitizeString} from 'hubbl-shared';
import moment from 'moment';
import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import Lottie from 'react-lottie';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {Api} from '../../api';
import assetAppLoadingAnimation from '../../assets/animations/app-loading/assetAppLoadingAnimation';
import {
  PaddingBottom,
  Scene,
  SocialActivitiesFilters,
  SocialActivitiesTimeline,
  TextBodyMedium,
  TextHeading3,
  WelcomeSection,
  type FilterCounts,
  type FilterState,
  type SocialActivity,
} from '../../components';
import {useAppDispatch, useAppSelector, useI18n} from '../../hooks';
import {EventDTO} from '../../models';
import {RootRoute} from '../../routes/RootRoute';
import {CompanySelector} from '../../store/company/CompanySelector';
import {EventSelector} from '../../store/events/EventSelector';
import {fetchFutureEvents} from '../../store/events/EventThunks';
import {HubblSelector} from '../../store/hubbl/HubblSelector';
import {UserSelector} from '../../store/user/UserSelector';
import {getAuthUserCompanyId} from '../../utils';
import {EvaluationModal} from '../event/components/right-panel/components/evaluation-modal/EvaluationModal';

// Local storage utility functions
const FILTERS_STORAGE_KEY = 'socialActivitiesFilters'; // Key for storing filter preferences in localStorage

const loadFiltersFromStorage = (): FilterState | null => {
  try {
    const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      // Ensure showMyOwn is always false by default (shows "All")
      return {
        ...parsed,
        showMyOwn: false,
      };
    }
    return null;
  } catch (error) {
    console.error('Error loading filters from local storage:', error);
    return null;
  }
};

const saveFiltersToStorage = (filters: FilterState): void => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving filters to local storage:', error);
  }
};

export const SceneEmployeeHomeInternal = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const t = useI18n();
  const {loading, hasLoadedFutureEvents} = useAppSelector(
    EventSelector.getEvents,
  );
  const futureEvents = useAppSelector(EventSelector.getFutureEvents);

  const user = useAppSelector(UserSelector.getUserDetails);
  const company = useAppSelector(CompanySelector.getCompanyDetails);

  // Get data from Redux store
  const pendingHubblGroups = useAppSelector(
    HubblSelector.getPendingHubblGroups,
  );

  const scheduledHubblGroups = useAppSelector(
    HubblSelector.getScheduledHubblGroups,
  );

  const pendingBreaks = useAppSelector(HubblSelector.getPendingBreaks);

  const scheduledBreaks = useAppSelector(HubblSelector.getScheduledBreaks);

  // Loading states
  const pendingHubblesLoading = useAppSelector(
    HubblSelector.getPendingHubblesLoading,
  );
  const scheduledHubblesLoading = useAppSelector(
    HubblSelector.getScheduledHubblesLoading,
  );

  // Has loaded states to prevent refetching
  const pendingHubblesHasLoaded = useAppSelector(
    HubblSelector.getPendingHubblesHasLoaded,
  );
  const scheduledHubblesHasLoaded = useAppSelector(
    HubblSelector.getScheduledHubblesHasLoaded,
  );

  // Create social activities from Redux store data
  const socialActivities = useMemo(() => {
    const now = new Date();

    const activities: SocialActivity[] = [
      // Add events
      ...futureEvents.map(event => ({type: 'event' as const, item: event})),
      // Add groups (pending + scheduled)
      ...pendingHubblGroups.map(group => ({
        type: 'group' as const,
        item: group,
      })),
      ...scheduledHubblGroups.map(group => ({
        type: 'group' as const,
        item: group,
      })),
      // Add connects (pending + scheduled) - filter out passed connects
      ...pendingBreaks
        .filter(breakItem => {
          const startTime = breakItem.startDateTime.toDate();
          return startTime > now;
        })
        .map(breakItem => ({
          type: 'connect' as const,
          item: breakItem,
        })),
      ...scheduledBreaks
        .filter(breakItem => {
          const startTime = breakItem.startDateTime.toDate();
          return startTime > now;
        })
        .map(breakItem => ({
          type: 'connect' as const,
          item: breakItem,
        })),
    ];

    return activities;
  }, [
    futureEvents,
    pendingHubblGroups,
    scheduledHubblGroups,
    pendingBreaks,
    scheduledBreaks,
  ]);

  // State for evaluation modal
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [currentEvaluationEvent, setCurrentEvaluationEvent] =
    useState<EventDTO | null>(null);
  const [pendingEvaluationEvents, setPendingEvaluationEvents] = useState<
    EventDTO[]
  >([]);
  const [hasCheckedEvaluations, setHasCheckedEvaluations] = useState(false);

  // Filter state with local storage persistence
  const [filters, setFilters] = useState<FilterState>(() => {
    // Load from local storage on initialization
    const savedFilters = loadFiltersFromStorage();
    if (savedFilters) {
      return savedFilters;
    }
    // Default filters if no saved data
    return {
      showDeclined: false,
      showAccepted: true,
      showPending: true,
      showEvents: true,
      showGroups: true,
      showConnects: true,
      showMyOwn: false,
      searchString: '',
    };
  });

  // Check for pending evaluations when user and company data are available
  useEffect(() => {
    const checkPendingEvaluations = async () => {
      if (!user || !company || hasCheckedEvaluations) return;

      try {
        const companyId = await getAuthUserCompanyId();
        const {events: evaluationEvents} =
          await Api.event.getEventsForEvaluation(companyId, user.id);

        if (evaluationEvents.length > 0) {
          setPendingEvaluationEvents(evaluationEvents);
          // Auto-open modal for the first pending evaluation
          setCurrentEvaluationEvent(evaluationEvents[0]);
          setIsEvaluationModalOpen(true);
        }

        setHasCheckedEvaluations(true);
      } catch (error) {
        console.error('Error checking pending evaluations:', error);
        setHasCheckedEvaluations(true);
      }
    };

    checkPendingEvaluations();
  }, [user, company, hasCheckedEvaluations]);

  const handleEvaluationSuccess = useCallback(async () => {
    if (!currentEvaluationEvent) return;

    // Remove the completed evaluation from pending list
    const remainingEvaluations = pendingEvaluationEvents.filter(
      event => event.id !== currentEvaluationEvent.id,
    );
    setPendingEvaluationEvents(remainingEvaluations);

    // If there are more evaluations, show the next one
    if (remainingEvaluations.length > 0) {
      setCurrentEvaluationEvent(remainingEvaluations[0]);
      // Keep modal open for next evaluation
    } else {
      // No more evaluations, close modal
      setIsEvaluationModalOpen(false);
      setCurrentEvaluationEvent(null);
    }

    // Refresh future events to reflect any changes
    dispatch(fetchFutureEvents({isAdmin: false}));
  }, [currentEvaluationEvent, pendingEvaluationEvents, dispatch]);

  const handleEvaluationClose = useCallback(() => {
    setIsEvaluationModalOpen(false);
    // Note: We don't clear currentEvaluationEvent so user can resume later
  }, []);

  // Handle filter changes and save to local storage
  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    saveFiltersToStorage(newFilters);
  }, []);

  // Helper function to determine user's response status
  const getUserResponseStatus = useCallback(
    (activity: SocialActivity) => {
      if (!user) return 'pending';

      switch (activity.type) {
        case 'event':
          const event = activity.item;
          if (event.declinedUserIds?.includes(user.id)) return 'declined';
          if (event.attendees?.some(attendee => attendee.id === user.id))
            return 'accepted';
          return 'pending';

        case 'group':
          const group = activity.item;
          if (group.declinedUserIds?.includes(user.id)) return 'declined';
          if (group.attendees?.some(attendee => attendee.id === user.id))
            return 'accepted';
          return 'pending';

        case 'connect':
          const connect = activity.item;
          if (connect.declinedUserIds?.includes(user.id)) return 'declined';
          // For connects, user is accepted if they are creator or acceptor
          if (
            connect[BreakKey.UserCreator]?.id === user.id ||
            connect[BreakKey.UserAcceptor]?.id === user.id
          )
            return 'accepted';
          return 'pending';

        default:
          return 'pending';
      }
    },
    [user],
  );

  // Helper function to check if activity is created by current user
  const isActivityCreatedByUser = useCallback(
    (activity: SocialActivity): boolean => {
      if (!user) return false;

      switch (activity.type) {
        case 'event':
          // Events don't have a creator field in the same way
          return false;
        case 'group':
          return activity.item.creator?.id === user.id;
        case 'connect':
          return activity.item[BreakKey.UserCreator]?.id === user.id;
        default:
          return false;
      }
    },
    [user],
  );

  // Create filtered social activities from Redux store
  const filteredSocialActivities = useMemo(() => {
    // Filter by type first
    const typeFilteredActivities = socialActivities.filter(activity => {
      if (activity.type === 'event' && !filters.showEvents) return false;
      if (activity.type === 'group' && !filters.showGroups) return false;
      if (activity.type === 'connect' && !filters.showConnects) return false;
      return true;
    });

    // Filter by response status
    const statusFilteredActivities = typeFilteredActivities.filter(activity => {
      const status = getUserResponseStatus(activity);

      if (status === 'declined' && !filters.showDeclined) return false;
      if (status === 'accepted' && !filters.showAccepted) return false;
      if (status === 'pending' && !filters.showPending) return false;

      return true;
    });

    // Filter by ownership
    const ownershipFilteredActivities = statusFilteredActivities.filter(
      activity => {
        if (filters.showMyOwn) {
          return isActivityCreatedByUser(activity);
        }
        return true;
      },
    );

    // Filter by search string
    const searchFilteredActivities = ownershipFilteredActivities.filter(
      activity => {
        if (!filters.searchString || !filters.searchString.trim()) {
          return true;
        }

        const sanitizedSearchString = sanitizeString(filters.searchString);

        if (activity.type === 'event') {
          const event = activity.item;
          return (
            sanitizeString(event.eventTitle?.en || '').includes(
              sanitizedSearchString,
            ) ||
            sanitizeString(event.eventDescription?.en || '').includes(
              sanitizedSearchString,
            ) ||
            sanitizeString(event.eventLocation || '').includes(
              sanitizedSearchString,
            ) ||
            event.eventTags?.some(tag =>
              sanitizeString(tag).includes(sanitizedSearchString),
            )
          );
        } else if (activity.type === 'group') {
          const group = activity.item;
          return (
            sanitizeString(group.description?.en || '').includes(
              sanitizedSearchString,
            ) ||
            sanitizeString(group.creator?.fullName || '').includes(
              sanitizedSearchString,
            ) ||
            sanitizeString(group.location || '').includes(sanitizedSearchString)
          );
        } else if (activity.type === 'connect') {
          const connect = activity.item;
          return (
            connect.activities?.some(activity =>
              sanitizeString(activity.label?.en || '').includes(
                sanitizedSearchString,
              ),
            ) ||
            sanitizeString(connect.userCreator?.fullName || '').includes(
              sanitizedSearchString,
            ) ||
            sanitizeString(connect.userAcceptor?.fullName || '').includes(
              sanitizedSearchString,
            )
          );
        }

        return false;
      },
    );

    // Sort by start date
    const sortedActivities = searchFilteredActivities.sort((a, b) => {
      const aDate = moment(a.item.startDateTime.toDate());
      const bDate = moment(b.item.startDateTime.toDate());

      return aDate.diff(bDate);
    });

    return sortedActivities;
  }, [
    socialActivities,
    filters,
    getUserResponseStatus,
    isActivityCreatedByUser,
  ]);

  // Calculate counts for each filter type
  const filterCounts = useMemo((): FilterCounts => {
    const counts: FilterCounts = {
      pending: 0,
      accepted: 0,
      declined: 0,
      events: 0,
      groups: 0,
      connects: 0,
      myOwn: 0,
    };

    // Determine which activities to count based on current filter state
    const activitiesToCount = filters.showMyOwn
      ? socialActivities.filter(activity => isActivityCreatedByUser(activity))
      : socialActivities;

    activitiesToCount.forEach(activity => {
      const status = getUserResponseStatus(activity);

      // Count by status
      if (status === 'pending') counts.pending++;
      else if (status === 'accepted') counts.accepted++;
      else if (status === 'declined') counts.declined++;

      // Count by type
      if (activity.type === 'event') counts.events++;
      else if (activity.type === 'group') counts.groups++;
      else if (activity.type === 'connect') counts.connects++;
    });

    // Always count total "My Own" activities from all activities
    socialActivities.forEach(activity => {
      if (isActivityCreatedByUser(activity)) counts.myOwn++;
    });

    return counts;
  }, [
    socialActivities,
    getUserResponseStatus,
    isActivityCreatedByUser,
    filters.showMyOwn,
  ]);

  const handleEventClick = (eventId: string) => {
    navigate(`${RootRoute.EmployeeEvent.replace(':eventId', eventId)}`);
  };

  return (
    <Scene>
      <WelcomeSection
        title={t.scenes.dashboard.welcomeBack.replace(
          '{name}',
          user?.firstName || '',
        )}
        description={t.scenes.dashboard.upcomingActivities.replace(
          '{companyName}',
          company?.companyName ?? 'your company',
        )}
      />

      {(loading && !hasLoadedFutureEvents) ||
      (pendingHubblesLoading && !pendingHubblesHasLoaded) ||
      (scheduledHubblesLoading && !scheduledHubblesHasLoaded) ? (
        <EventsLoadingContainer>
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: assetAppLoadingAnimation,
              rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice',
              },
            }}
            height={80}
            width={80}
          />
          <TextBodyMedium text={t.dashboard.loadingSocialActivities} />
        </EventsLoadingContainer>
      ) : (
        <>
          {/* Filters */}
          <SocialActivitiesFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            counts={filterCounts}
          />

          {/* Social Activities Section */}
          {filteredSocialActivities.length > 0 ? (
            <>
              <SectionHeader>
                <TextHeading3 text={t.dashboard.socialActivities} />
              </SectionHeader>
              <PaddingBottom amount={16} />

              <SocialActivitiesTimeline
                activities={filteredSocialActivities}
                onEventClick={handleEventClick}
              />
            </>
          ) : (
            <EmptyState>
              <div style={{fontSize: '4rem', marginBottom: '1rem'}}>🎯</div>
              <TextHeading3 text={t.dashboard.noSocialActivities} />
              <PaddingBottom amount={16} />
              <TextBodyMedium text={t.dashboard.noActivitiesMatchFilters} />
            </EmptyState>
          )}
        </>
      )}

      {/* Auto-opened Evaluation Modal */}
      {currentEvaluationEvent &&
        currentEvaluationEvent.evaluation &&
        currentEvaluationEvent.evaluation.questions &&
        currentEvaluationEvent.evaluation.questions.length > 0 &&
        user && (
          <EvaluationModal
            evaluationCustomDescription={
              currentEvaluationEvent.evaluation.customDescription
            }
            isOpen={isEvaluationModalOpen}
            onClose={handleEvaluationClose}
            eventId={currentEvaluationEvent.id}
            eventTitle={`${currentEvaluationEvent.eventTitle[user?.settings.preferredLanguage || 'en']} ${currentEvaluationEvent.eventEmoji}`}
            companyId={currentEvaluationEvent.companyId}
            questions={currentEvaluationEvent.evaluation.questions}
            onSuccess={handleEvaluationSuccess}
          />
        )}
    </Scene>
  );
});

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
  padding-top: 8px;
`;

const EventsLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;
